// Route identity of the legacy (webpack) app for a URL, for `@s-ui/react-web-vitals`.
//
// The reporter reads `useRouter().routes` and only reports a page whose last matched route has an
// allowed `path` or `id` (`WEB_VITALS_PATHNAMES`), tagging every metric with that path and id. The RR7
// shim has no such list, and the RR7 route table neither has those ids nor resolves ad details the
// same way (one catch-all), so instead of touching routing this resolves the URL against a copy of the
// legacy route table: same order, same patterns, same ids, generated from the legacy `src/routes.js`.
// The matching is a port of `@s-ui/react-router` (`PatternUtils.matchPattern` and the reducer of
// `internal/createTransitionManager.js`), so every metric keeps the path/id it had before the cutover.
import {
  ADS_DETAIL_COCHES_REGEX,
  ADS_DETAIL_COCHES_SUBSCRIPTION_REGEX,
  ADS_DETAIL_MOTOS_REGEX,
  APP_NAMES,
  NEWS_COMMENTS_PATH_PATTERN,
  NEWS_DETAIL_REGEX,
  ROUTE_IDS,
  ROUTES
} from '../config/index.js'

// The legacy bundle transpiled named groups away, so the reporter's `regexp.toString()` path tag had
// no group names. Drop them here too (same matches) so the tag stays what the dashboards know.
const withoutGroupNames = regexp => new RegExp(regexp.source.replace(/\(\?<[^>]+>/g, '('), regexp.flags)

const getLegacyRouteTree = ({isMotos}) => {
  const appName = isMotos ? APP_NAMES.MOTOS : APP_NAMES.COCHES
  return {
    level: 1,
    children: [
      {path: ROUTES.HOME, id: ROUTE_IDS.HOME},
      {regexp: withoutGroupNames(ADS_DETAIL_COCHES_REGEX), id: ROUTE_IDS.AD_DETAIL_COCHES},
      {regexp: withoutGroupNames(ADS_DETAIL_COCHES_SUBSCRIPTION_REGEX)},
      {regexp: withoutGroupNames(ADS_DETAIL_MOTOS_REGEX), id: ROUTE_IDS.AD_DETAIL_MOTOS},
      {path: ROUTES.DETAIL.AD, id: ROUTE_IDS.AD_DETAIL_REDIRECT},
      {path: ROUTES.RATING},
      {path: ROUTES.INSTANT_OFFER.COCHES},
      {path: ROUTES.INSTANT_OFFER.MOTOS},
      {path: ROUTES.RECOMMENDATIONS},
      {path: ROUTES.INSTANT_OFFER_NEXT_APPOINTMENT},
      {path: ROUTES.INSURANCE},
      {path: ROUTES.LOGIN},
      {path: ROUTES.MESSAGING_ACTION_PROXY.C2B_SELLER},
      {path: ROUTES.MESSAGING_ACTION_PROXY.C2B_BUYER},
      {path: ROUTES.MESSAGING_ACTION_PROXY.RESERVATION_SELLER},
      {path: ROUTES.MESSAGING_ACTION_PROXY.RESERVATION_BUYER},
      {path: ROUTES.MESSAGING_ACTION_PROXY.TRANSACTION_SELLER},
      {path: ROUTES.MESSAGING_ACTION_PROXY.TRANSACTION_BUYER},
      {path: ROUTES.AD_MANAGEMENT.MY_ADS},
      {path: ROUTES.USER.TAKEOUT},
      {path: ROUTES.USER.FAVORITES},
      {path: ROUTES.USER.ALERT, id: ROUTE_IDS.USER_ALERT},
      {path: ROUTES.USER.ALERTS, id: ROUTE_IDS.USER_ALERTS},
      {path: ROUTES.USER.ALERTS_MATCHES},
      {path: ROUTES.USER.DISCOUNTS},
      {path: ROUTES.USER.PROFILE_COMPLETE.PURCHASE},
      {path: ROUTES.USER.PROFILE},
      {path: ROUTES.USER.PROFILE_EDIT},
      {path: ROUTES.USER.PHONE_VERIFICATION},
      {path: ROUTES.USER.UNSUBSCRIBE, id: ROUTE_IDS.USER_UNSUBSCRIBE},
      {path: ROUTES.TRANSACTION.BUYER_CHECK_COSTS_LIST},
      {path: ROUTES.TRANSACTION.BUYER_ID},
      {path: ROUTES.TRANSACTION.BUYER_ID_UPLOAD_BACK},
      {path: ROUTES.TRANSACTION.BUYER_ID_UPLOAD_FRONT},
      {path: ROUTES.TRANSACTION.BUYER_IDENTITY},
      {path: ROUTES.TRANSACTION.BUYER_INIT_PAYMENT},
      {path: ROUTES.TRANSACTION.BUYER_PAYMENT_FAIL},
      {path: ROUTES.TRANSACTION.BUYER_PAYMENT_SUCCESS},
      {path: ROUTES.TRANSACTION.BUYER_PROFESSION},
      {path: ROUTES.TRANSACTION.BUYER_SET_PASSCODE},
      {path: ROUTES.TRANSACTION.SELLER_ACCOUNT},
      {path: ROUTES.TRANSACTION.SELLER_ID},
      {path: ROUTES.TRANSACTION.SELLER_IDENTITY},
      {path: ROUTES.TRANSACTION.SELLER_ID_UPLOAD_BACK},
      {path: ROUTES.TRANSACTION.SELLER_ID_UPLOAD_FRONT},
      {path: ROUTES.TRANSACTION.PRICE_CONFIRM},
      {path: ROUTES.TRANSACTION.PRICE},
      {path: ROUTES.TRANSACTION.TERMS},
      {path: ROUTES.USER.UNSUBSCRIBE_EMAILS},
      {path: ROUTES.USER.UNSUBSCRIBE_FAVORITE},
      {path: ROUTES.RESERVATION.PAYMENT},
      {path: ROUTES.RESERVATION.PAYMENT_ERROR},
      {path: ROUTES.RESERVATION.PAYMENT_SUCCESS},
      {path: ROUTES.RESERVATION.PHONE_NUMBER_REQUIRED},
      {path: ROUTES.RESERVATION.REFUND},
      {path: ROUTES.RESERVATION.REFUND_REASON},
      {path: ROUTES.RESERVATION.REFUND_ERROR},
      {path: ROUTES.TERMS_RESERVATION},
      {path: ROUTES.TERMS},
      {path: ROUTES.TRANSPARENCY},
      {path: ROUTES.COOKIE_POLICY},
      {path: ROUTES.LANDING_INSTANT_OFFER},
      {path: ROUTES.INSTANT_OFFER_STANDALONE.COCHES},
      {path: ROUTES.INSTANT_OFFER_STANDALONE.MOTOS},
      {path: ROUTES.PAYMENT_AD},
      {path: ROUTES.PRODUCTS},
      {path: ROUTES.AD_PUBLISHER.PUBLISH_MY_AD_WITH_PLATE},
      {path: ROUTES.AD_PUBLISHER.PUBLISH_MY_AD_CATEGORY},
      {path: ROUTES.AD_PUBLISHER.PUBLISH_MY_AD},
      {path: ROUTES.SELL_MY_VEHICLE.COCHES.LOCATION},
      {path: ROUTES.SELL_MY_VEHICLE.MOTOS.LOCATION},
      {path: ROUTES.AD_PUBLISHER.EDIT_MY_AD},
      {path: ROUTES.DETAIL_PREVIEW},
      {path: ROUTES.STOCK},
      {path: ROUTES.STOCK_SUBSCRIPTION},
      {path: ROUTES.DEALERS_LIST.PROVINCE, id: ROUTE_IDS.DEALERS_LIST},
      {path: ROUTES.END_INTEGRATION},
      {path: ROUTES.FINANCING},
      {path: ROUTES.FORMALITIES.COCHES},
      {path: ROUTES.FORMALITIES.MOTOS},
      {path: ROUTES.FORMALITIES_SECURITY_GUIDELINES.COCHES},
      {path: ROUTES.FORMALITIES_SECURITY_GUIDELINES.MOTOS},
      {path: ROUTES.TECHNICAL_DETAILS.INDEX},
      {path: ROUTES.TECHNICAL_DETAILS.COMPARATOR, id: ROUTE_IDS.TECHNICAL_DETAILS_COMPARATOR},
      {path: ROUTES.TECHNICAL_DETAILS.COMPARATOR_RESULTS, id: ROUTE_IDS.TECHNICAL_DETAILS_COMPARATOR_RESULTS},
      {path: ROUTES.TECHNICAL_DETAILS.SEARCH},
      {path: ROUTES.TECHNICAL_DETAILS.MAKE},
      {path: ROUTES.TECHNICAL_DETAILS.MODEL},
      {path: ROUTES.SELLER_SECURE_PAYMENT_BENEFITS},
      {path: ROUTES.SECURE_PAYMENT_KYC},
      {path: ROUTES.MAKES_SIZES_DISTRIBUTOR},
      {path: ROUTES.MAKE},
      {path: ROUTES.MAKES_DISTRIBUTOR},
      {path: ROUTES.MODEL_SIZES, id: ROUTE_IDS.MODEL_SIZES},
      {path: ROUTES.MODEL_DETAIL_INTERIOR_SIZES},
      {path: ROUTES.MODEL_DETAIL_TRUNK_SIZES},
      {path: ROUTES.VEHICLE_CONFIGURATOR_V3.INDEX},
      {path: ROUTES.VEHICLE_CONFIGURATOR_V3.FUEL_TYPE},
      {path: ROUTES.VEHICLE_CONFIGURATOR_V3.TRIM_LEVEL},
      {path: ROUTES.VEHICLE_CONFIGURATOR_V3.ENGINE},
      {path: ROUTES.VEHICLE_CONFIGURATOR_V3.COLOR},
      {path: ROUTES.VEHICLE_CONFIGURATOR_V3.FORM},
      {path: ROUTES.VEHICLE_CONFIGURATOR_V3.RESULTS},
      {path: ROUTES.VEHICLE_SUGGESTER.INDEX},
      {path: ROUTES.VEHICLE_SUGGESTER.BODY_TYPE},
      {path: ROUTES.VEHICLE_SUGGESTER.FUEL_TYPE},
      {path: ROUTES.VEHICLE_SUGGESTER.SEATS},
      {path: ROUTES.VEHICLE_SUGGESTER.MAKES},
      {path: ROUTES.VEHICLE_SUGGESTER.RESULTS},
      {path: ROUTES.WDYL, id: ROUTE_IDS.MODEL_DETAIL},
      ...(isMotos
        ? [
            {path: ROUTES.TECHNICAL_DETAILS.VEHICLE_WITHOUT_VERSION},
            {path: ROUTES.TECHNICAL_DETAILS.VEHICLE[APP_NAMES.MOTOS]}
          ]
        : [
            {path: ROUTES.TECHNICAL_DETAILS.BODY_TYPE},
            {path: ROUTES.TECHNICAL_DETAILS.VERSION_EMPTY},
            {path: ROUTES.TECHNICAL_DETAILS.VERSION},
            {path: ROUTES.TECHNICAL_DETAILS.VEHICLE[APP_NAMES.COCHES]}
          ]),
      {path: ROUTES.VEHICLE_APPRAISER},
      {path: NEWS_COMMENTS_PATH_PATTERN},
      {regexp: withoutGroupNames(NEWS_DETAIL_REGEX[appName]), id: ROUTE_IDS.NEWS_DETAIL},
      {path: ROUTES.NEWS_PREVIEW.DETAIL},
      {
        path: ROUTES.NEWS.INDEX,
        id: ROUTE_IDS.NEWS_LIST,
        children: [
          {index: true},
          {path: ROUTES.NEWS.TAG, id: ROUTE_IDS.NEWS_LIST_TAGS},
          {path: ROUTES.NEWS.CATEGORY, id: ROUTE_IDS.NEWS_LIST_CATEGORY}
        ]
      },
      {
        path: ROUTES.CHECKOUT.INDEX,
        children: [
          {index: true},
          {path: ROUTES.CHECKOUT.SUCCESS},
          {path: ROUTES.CHECKOUT.ERROR},
          {path: ROUTES.CHECKOUT.PENDING},
          {path: ROUTES.CHECKOUT.REFUND_ERROR},
          {path: ROUTES.CHECKOUT.ACTIVATION_ERROR},
          {path: ROUTES.CHECKOUT.REQUEST_ERROR},
          {path: ROUTES.CHECKOUT.UNKNOWN_ERROR}
        ]
      },
      {path: ROUTES.COMPARATOR},
      {path: ROUTES.NET},
      {path: ROUTES.ADS_LIST_SUBSCRIPTION, id: ROUTE_IDS.ADS_LIST_SUBSCRIPTION},
      {path: ROUTES.ADS_LIST_SEARCH, id: ROUTE_IDS.ADS_LIST_SEARCH},
      {path: ROUTES.DEFAULT, id: ROUTE_IDS.ADS_LIST}
    ]
  }
}

// --- Port of @s-ui/react-router PatternUtils.matchPattern ---
const PATTERN_TOKEN = /:([a-zA-Z_$][a-zA-Z0-9_$]*)|\*\*|\*|\(|\)|\\\(|\\\)/g
const escapeRegExp = string => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
const compiledPatterns = new Map()

const compilePattern = pattern => {
  if (compiledPatterns.has(pattern)) return compiledPatterns.get(pattern)
  let regexpSource = ''
  const tokens = []
  let match
  let lastIndex = 0
  PATTERN_TOKEN.lastIndex = 0
  while ((match = PATTERN_TOKEN.exec(pattern))) {
    if (match.index !== lastIndex) {
      tokens.push(pattern.slice(lastIndex, match.index))
      regexpSource += escapeRegExp(pattern.slice(lastIndex, match.index))
    }
    if (match[1]) regexpSource += '([^/]+)'
    else if (match[0] === '**') regexpSource += '(.*)'
    else if (match[0] === '*') regexpSource += '(.*?)'
    else if (match[0] === '(') regexpSource += '(?:'
    else if (match[0] === ')') regexpSource += ')?'
    else if (match[0] === '\\(') regexpSource += '\\('
    else if (match[0] === '\\)') regexpSource += '\\)'
    tokens.push(match[0])
    lastIndex = PATTERN_TOKEN.lastIndex
  }
  if (lastIndex !== pattern.length) {
    tokens.push(pattern.slice(lastIndex))
    regexpSource += escapeRegExp(pattern.slice(lastIndex))
  }
  const compiled = {regexpSource, tokens}
  compiledPatterns.set(pattern, compiled)
  return compiled
}

const matchPattern = (rawPattern, pathname) => {
  const pattern = rawPattern.charAt(0) === '/' ? rawPattern : `/${rawPattern}`
  let {regexpSource} = compilePattern(pattern)
  const {tokens} = compilePattern(pattern)
  if (pattern.charAt(pattern.length - 1) !== '/') regexpSource += '/?'
  if (tokens[tokens.length - 1] === '*') regexpSource += '$'
  const match = pathname.match(new RegExp(`^${regexpSource}`, 'i'))
  if (!match) return null
  const [matchedPath] = match
  let remainingPathname = pathname.substring(matchedPath.length)
  if (remainingPathname) {
    if (matchedPath.charAt(matchedPath.length - 1) !== '/') return null
    remainingPathname = `/${remainingPathname}`
  }
  return {remainingPathname}
}

// --- Port of the route reducer of @s-ui/react-router internal/createTransitionManager ---
const withLevels = (node, level = 1) => ({
  ...node,
  level,
  ...(node.children && {children: node.children.map(child => withLevels(child, level + 1))})
})

const isIntact = nodes => nodes.every((node, index) => node.level === index + 1)

const reduceTree = (reducer, acc, node) =>
  (node.children || []).reduce((childAcc, child) => reduceTree(reducer, childAcc, child), reducer(acc, node))

const createReducer = pathname => (acc, node) => {
  if (acc.isFinished || node.index) return acc
  const nodes = acc.nodes.filter(previous => previous.level < node.level)
  if (node.regexp) {
    const match = acc.remainingPathname.match(node.regexp)
    if (!match) return {...acc, nodes}
    const remainingPathname = match.input.replace(match[0], '')
    const next = {...acc, nodes: [...nodes, node], remainingPathname}
    return remainingPathname === '' ? {...next, isFinished: isIntact(next.nodes)} : next
  }
  const pattern = node.path || ''
  if (pattern === '') return {...acc, nodes: [...nodes, node]}
  const remaining = pattern.charAt(0) === '/' ? pathname : acc.remainingPathname
  const matched = matchPattern(pattern, remaining)
  if (!matched) return {...acc, nodes}
  const next = {...acc, nodes: [...nodes, node], remainingPathname: matched.remainingPathname}
  return matched.remainingPathname === '' ? {...next, isFinished: isIntact(next.nodes)} : next
}

const trees = {}
const resolved = new Map()
const MAX_RESOLVED = 50

/**
 * Matched legacy routes for a pathname, outermost first, as `routeInfo.routes` of the legacy router:
 * the last one carries the `path` (or `regexp`) and `id` the web-vitals reporter tags metrics with.
 * The same array is returned for the same pathname, so a page that rewrites `route.id` (the ad detail
 * variant) rewrites the object the reporter reads.
 */
export const getLegacyRoutes = ({pathname, isMotos}) => {
  const key = `${isMotos ? 'motos' : 'coches'}:${pathname}`
  if (resolved.has(key)) return resolved.get(key)
  const tenant = isMotos ? 'motos' : 'coches'
  trees[tenant] ??= withLevels(getLegacyRouteTree({isMotos}))
  const path = pathname.charAt(0) === '/' ? pathname : `/${pathname}`
  const match = reduceTree(createReducer(path), {isFinished: false, nodes: [], remainingPathname: path}, trees[tenant])
  const routes = match.remainingPathname ? [] : match.nodes.map(({children, ...route}) => route)
  if (resolved.size >= MAX_RESOLVED) resolved.delete(resolved.keys().next().value)
  resolved.set(key, routes)
  return routes
}
